import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchCurrencyRate } from "@/lib/api";

const AppContext = createContext(null);

const CART_KEY = "nova_cart_v1";
const CURR_KEY = "nova_currency_v1";
const FIRST_ORDER_KEY = "helixora_first_order_v1";  // "true" until customer places a paid order

function loadCart() {
    try {
        const raw = localStorage.getItem(CART_KEY);
        const items = raw ? JSON.parse(raw) : [];
        // EU warehouse was retired on Feb 25 2026 — drop any orphaned EU cart items.
        return Array.isArray(items) ? items.filter((i) => (i?.region || "").toUpperCase() !== "EU") : [];
    } catch (e) {
        console.debug("[cart] localStorage parse failed, starting empty", e);
        return [];
    }
}

function loadFirstOrder() {
    try {
        const raw = localStorage.getItem(FIRST_ORDER_KEY);
        return raw === null ? true : raw === "true";
    } catch (_) { return true; }
}

export function AppProvider({ children }) {
    const [cart, setCart] = useState(loadCart);
    const [currency, setCurrencyState] = useState(() => localStorage.getItem(CURR_KEY) || "USD");
    const [cadRate, setCadRate] = useState(1.37);

    // Auto-detect by timezone on first mount if no preference saved
    useEffect(() => {
        if (!localStorage.getItem(CURR_KEY)) {
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
                if (tz.toLowerCase().includes("toronto") ||
                    tz.toLowerCase().includes("vancouver") ||
                    tz.toLowerCase().includes("edmonton") ||
                    tz.toLowerCase().includes("halifax") ||
                    tz.toLowerCase().includes("winnipeg") ||
                    tz.toLowerCase().includes("regina") ||
                    tz.toLowerCase().includes("st_johns")) {
                    setCurrencyState("CAD");
                    localStorage.setItem(CURR_KEY, "CAD");
                }
            } catch (_) { /* noop */ }
        }
    }, []);

    useEffect(() => {
        fetchCurrencyRate().then((r) => {
            if (r?.CAD) setCadRate(r.CAD);
        }).catch((e) => console.debug("[currency] live FX fetch failed, using fallback 1.37", e));
    }, []);

    useEffect(() => {
        localStorage.setItem(CART_KEY, JSON.stringify(cart));
    }, [cart]);

    const setCurrency = useCallback((c) => {
        setCurrencyState(c);
        localStorage.setItem(CURR_KEY, c);
    }, []);

    const addToCart = useCallback((product, qty = 1) => {
        setCart((prev) => {
            const existing = prev.find((i) => i.product_id === product.id);
            if (existing) {
                return prev.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i);
            }
            return [...prev, {
                product_id: product.id,
                quantity: qty,
                name: product.name,
                dosage: product.dosage,
                price_usd: (product.sale_active && product.sale_price_usd) ? product.sale_price_usd : product.price_usd,
                original_price_usd: product.price_usd,
                on_sale: !!(product.sale_active && product.sale_price_usd),
                image: product.image,
                molecule: product.molecule || null,
                slug: product.slug,
                variant_group: product.variant_group || null,
                variant_label: product.variant_label || null,
                region: (product.region || "NA").toUpperCase(),
                price_eur: product.price_eur || null,
                made_to_order: !!product.made_to_order,
                made_to_order_fee_usd: product.made_to_order_fee_usd || 0,
                made_to_order_lead_time: product.made_to_order_lead_time || "",
            }];
        });
    }, []);

    const updateQty = useCallback((product_id, quantity) => {
        setCart((prev) => prev.map((i) => i.product_id === product_id ? { ...i, quantity: Math.max(1, quantity) } : i));
    }, []);

    const removeFromCart = useCallback((product_id) => {
        setCart((prev) => prev.filter((i) => i.product_id !== product_id));
    }, []);

    const clearCart = useCallback(() => setCart([]), []);

    const formatPrice = useCallback((usd) => {
        if (currency === "CAD") {
            const val = usd * cadRate;
            return `CA$${val.toFixed(2)}`;
        }
        return `$${usd.toFixed(2)} USD`;
    }, [currency, cadRate]);

    const cartCount = cart.reduce((a, i) => a + i.quantity, 0);
    // BAC water bundle — when cart contains BAC water + at least one other peptide,
    // the BAC water unit price drops from $19 to $10 (server enforces same rule at checkout).
    const _bacBundleActive = cart.some((i) => i.product_id === "bac-water-10ml") && cart.some((i) => i.product_id !== "bac-water-10ml");
    const effectiveItemPriceUsd = (item) => (item.product_id === "bac-water-10ml" && _bacBundleActive) ? 10.0 : item.price_usd;
    const cartTotalUsd = cart.reduce((a, i) => a + effectiveItemPriceUsd(i) * i.quantity, 0);

    // Bundle discount tiers — keyed by variant_group. Mirrors backend BUNDLE_RULES so the
    // cart UI shows the correct % before checkout. Server is the source of truth.
    const DEFAULT_BUNDLE_TIERS = [
        { min: 10, pct: 0.20, label: "10-Pack Bulk" },
        { min: 5, pct: 0.15, label: "5-Vial Stack" },
        { min: 3, pct: 0.10, label: "3-Vial Mix" },
    ];
    const BUNDLE_RULES_BY_GROUP = {
        retatrutide: DEFAULT_BUNDLE_TIERS,
        hgh: [
            { min: 5, pct: 0.20, label: "5+ HGH Kits Bulk" },
            { min: 3, pct: 0.15, label: "3-Kit HGH Stack" },
        ],
    };
    const bundleDiscount = (() => {
        const groups = {};
        for (const item of cart) {
            const g = item.variant_group;
            if (!g) continue;
            if (!groups[g]) groups[g] = { qty: 0, subtotal: 0 };
            groups[g].qty += item.quantity;
            groups[g].subtotal += item.price_usd * item.quantity;
        }
        let discount = 0;
        const applied = [];
        for (const [g, info] of Object.entries(groups)) {
            const tiers = BUNDLE_RULES_BY_GROUP[g] || DEFAULT_BUNDLE_TIERS;
            const tier = tiers.find((t) => info.qty >= t.min);
            if (tier) {
                const d = info.subtotal * tier.pct;
                discount += d;
                applied.push({ group: g, qty: info.qty, pct: tier.pct, label: tier.label, discount_usd: d });
            }
        }
        return { discount_usd: discount, applied };
    })();

    const cartFinalUsd = Math.max(0, cartTotalUsd - bundleDiscount.discount_usd);

    // First-order 10% off — cookie-driven hint, server validates against email at checkout
    const [firstOrderHint, setFirstOrderHintState] = useState(loadFirstOrder);
    const setFirstOrderHint = useCallback((v) => {
        setFirstOrderHintState(v);
        try { localStorage.setItem(FIRST_ORDER_KEY, v ? "true" : "false"); } catch (_) { /* noop */ }
    }, []);
    const FIRST_ORDER_PCT = 0.10;
    const firstOrderDiscountUsd = firstOrderHint ? Math.max(0, cartFinalUsd * FIRST_ORDER_PCT) : 0;
    const cartFinalAfterFirstOrderUsd = Math.max(0, cartFinalUsd - firstOrderDiscountUsd);

    return (
        <AppContext.Provider value={{
            cart, addToCart, removeFromCart, updateQty, clearCart,
            cartCount, cartTotalUsd, cartFinalUsd, bundleDiscount,
            firstOrderHint, setFirstOrderHint,
            firstOrderDiscountUsd, cartFinalAfterFirstOrderUsd, FIRST_ORDER_PCT,
            currency, setCurrency, cadRate, formatPrice,
            bacBundleActive: _bacBundleActive,
            effectiveItemPriceUsd,
        }}>
            {children}
        </AppContext.Provider>
    );
}

export function useApp() {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used inside AppProvider");
    return ctx;
}
